import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

const Pagination = ({
    displayData,
    setDisplayData,
    pageNum,
    setPageNum,
    pageSize,
    setPageSize,
    totalRows,
    setTotalRows,
    source,
    serviceUrl_pagination,
    excludedFields,
    buttons,
    columnButtons,
    trigger,
    cellContent,
}) => {
    let page_num = pageNum;
    let page_size = pageSize;
    const [showDropdown, setShowDropdown] = useState(false);

    const headers = Object.keys(displayData[0]).filter(
        (header) => !excludedFields.includes(header)
    );

    if (buttons.length > 0) {
        headers.push("Actions");
    }

    const handleCellContent = (d, h, displayField) => {
        if (Array.isArray(d[h])) {
            return d[h].map(data => data[displayField]).join(", ");
        } else {
            return d[h];
        }
    };

    const [isLoading, setIsLoading] = useState(false);
    const [totalPages, setTotalPages] = useState(
        Math.ceil(totalRows / page_size)
    );

    // Create an array of page numbers
    let start_page = Math.max(1, pageNum - 2);
    let end_page = Math.min(totalPages, start_page + 4);
    if (end_page - start_page < 4) {
        start_page = Math.max(1, end_page - 4);
    }
    let pages = [];
    for (let i = start_page; i <= end_page; i++) {
        pages.push(i);
    }

    const handlePageSizeChange = (event) => {
        setPageSize(event.target.value);
        page_size = event.target.value;
        setTotalPages(Math.ceil(totalRows / event.target.value));
        handlePageNumChange(1);
    };

    const handlePageNumChange = (p) => {
        setPageNum(p);
        page_num = p;
        load_page();
    };

    const load_page = useCallback(async () => {
        try {
            setIsLoading(true);
            const params = {
                source: source,
                page_num: page_num,
                page_size: page_size,
            };
            const config = {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: localStorage.getItem("token"),
                },
            };
            const response = await axios.get(serviceUrl_pagination, {
                params,
                ...config,
            });
            if (response.data && Array.isArray(response.data.data)) {
                setDisplayData(response.data.data);
                if (response.data.total_rows) {
                    setTotalRows(response.data.total_rows);
                }
            } else {
                console.error(
                    `Expected response.data.data to be an array but received: ${JSON.stringify(response.data)}. Full response:`, response
                );
                // Handle the case where the response structure is unexpected
            }
        } catch (error) {
            console.error(
                `Failed to load page data from ${serviceUrl_pagination} with params: ${JSON.stringify({ source, page_num, page_size })}. Error:`,
                error
            );
        } finally {
            setIsLoading(false);
        }
    }, [source, page_num, page_size, serviceUrl_pagination, setDisplayData, setTotalRows]);
    const calculateRange = (pageNum, pageSize, totalRows) => {
        const startRecord = (pageNum - 1) * pageSize + 1;
        const endRecord = Math.min(pageNum * pageSize, totalRows);
        return `${startRecord} - ${endRecord} of ${totalRows} records`;
    };

    useEffect(() => {
        if (trigger !== 0) {
            load_page();
        }
    }, [trigger, load_page]);

    const uniqueId = `inputGroupSelect-${Math.random()
        .toString(36)
        .substr(2, 9)}`;

    return (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow">
            <div className="p-4">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-2 gap-2">
                    <div className="text-left">
                        {isLoading && (
                            <span
                                className="inline-block w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-2"
                                role="status"
                                aria-hidden="true"
                            ></span>
                        )}
                        Showing {calculateRange(pageNum, pageSize, totalRows)}
                    </div>
                    <div className="text-right">
                        <div className="relative inline-block">
                            <button
                                className="px-3 py-2 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors"
                                type="button"
                                onClick={() => setShowDropdown(!showDropdown)}
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                            </button>
                            {showDropdown && (
                                <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-slate-700 rounded-lg shadow-lg border border-gray-200 dark:border-slate-600 z-10">
                                    <div className="p-4">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" htmlFor={uniqueId}>
                                            Number of rows to display
                                        </label>
                                        <select
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            id={uniqueId}
                                            value={pageSize}
                                            disabled={isLoading}
                                            onChange={(event) => {
                                                handlePageSizeChange(event);
                                                setShowDropdown(false);
                                            }}
                                        >
                                            <option value="10">10</option>
                                            <option value="20">20</option>
                                            <option value="50">50</option>
                                        </select>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="overflow-x-auto table-scrollbar">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700 text-sm">
                        <thead className="bg-gray-50 dark:bg-slate-700">
                            <tr>
                                {headers.map((h, i) => (
                                    <th key={i} scope="col" className="px-3 py-3 text-left font-semibold text-gray-700 dark:text-white">
                                        {typeof h === 'object' ? JSON.stringify(h) : h[0].toUpperCase() + h.slice(1)}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-slate-700">
                            {displayData.map((d, i) => (
                                <tr key={i} className="hover:bg-gray-50 dark:hover:bg-slate-700">
                                    {headers.map((h, j) => (
                                        <td key={j} className="px-3 py-2 text-gray-900 dark:text-gray-100">
                                            {h === "Actions" ? (
                                                buttons.map((button, index) => {
                                                    const colorMap = {
                                                        'primary': 'bg-blue-600 hover:bg-blue-700 text-white',
                                                        'secondary': 'bg-gray-600 hover:bg-gray-700 text-white',
                                                        'danger': 'bg-red-600 hover:bg-red-700 text-white',
                                                        'success': 'bg-green-600 hover:bg-green-700 text-white',
                                                        'warning': 'bg-yellow-600 hover:bg-yellow-700 text-white',
                                                        'info': 'bg-cyan-600 hover:bg-cyan-700 text-white',
                                                        'outline-primary': 'border border-blue-600 text-blue-600 hover:bg-blue-50',
                                                        'outline-danger': 'border border-red-600 text-red-600 hover:bg-red-50',
                                                    };
                                                    return (
                                                        <button
                                                            key={index}
                                                            className={`px-3 py-1 text-sm rounded-lg transition-colors m-2 ${colorMap[button.color] || colorMap['secondary']} disabled:opacity-50 disabled:cursor-not-allowed`}
                                                            onClick={() => button.onClick(d)}
                                                            disabled={button.disable && button.disable(d)}
                                                        >
                                                            {button.label}
                                                        </button>
                                                    );
                                                })
                                            ) : (
                                                columnButtons && columnButtons.some(button => button.name === h) ? (
                                                    columnButtons.map((button) =>
                                                        button.name === h ? (
                                                            <button
                                                                key={button.name}
                                                                className={`px-3 py-1 text-sm rounded-lg transition-colors m-2 ${typeof button.color === 'function' ? button.color(d[h]) : 'bg-gray-600 hover:bg-gray-700 text-white'}`}
                                                                onClick={() => button.onClick ? button.onClick(d) : ""}
                                                            >
                                                                {d[h]}
                                                            </button>
                                                        ) : null
                                                    )
                                                ) : (
                                                    cellContent && cellContent.some(c => c.headerName === h) ? (
                                                        cellContent.map((c) =>
                                                            c.headerName === h ? handleCellContent(d, h, c.fieldName) : null
                                                        )
                                                    ) : (
                                                        d[h] === null ? "" : typeof d[h] === 'object' ? JSON.stringify(d[h]) : d[h] === null ? "" : d[h]
                                                    )
                                                )
                                            )}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <nav className="mt-4">
                    <ul className="flex justify-end items-center gap-1">
                        <li>
                            <button
                                className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                                    pageNum === 1 || isLoading
                                        ? 'bg-gray-100 dark:bg-slate-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                        : 'bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-600 border border-gray-300 dark:border-slate-600'
                                }`}
                                onClick={() => handlePageNumChange(1)}
                                disabled={pageNum === 1 || isLoading}
                            >
                                First
                            </button>
                        </li>
                        <li>
                            <button
                                className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                                    pageNum === 1 || isLoading
                                        ? 'bg-gray-100 dark:bg-slate-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                        : 'bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-600 border border-gray-300 dark:border-slate-600'
                                }`}
                                onClick={() => handlePageNumChange(pageNum - 1)}
                                disabled={pageNum === 1 || isLoading}
                            >
                                Previous
                            </button>
                        </li>
                        {pages.map((p, i) => (
                            <li key={i}>
                                <button
                                    className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                                        p === pageNum
                                            ? 'bg-blue-600 text-white'
                                            : isLoading
                                            ? 'bg-gray-100 dark:bg-slate-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                            : 'bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-600 border border-gray-300 dark:border-slate-600'
                                    }`}
                                    onClick={() => handlePageNumChange(p)}
                                    disabled={isLoading}
                                >
                                    {p}
                                </button>
                            </li>
                        ))}
                        <li>
                            <button
                                className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                                    pageNum === totalPages || isLoading
                                        ? 'bg-gray-100 dark:bg-slate-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                        : 'bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-600 border border-gray-300 dark:border-slate-600'
                                }`}
                                onClick={() => handlePageNumChange(pageNum + 1)}
                                disabled={pageNum === totalPages || isLoading}
                            >
                                Next
                            </button>
                        </li>
                        <li>
                            <button
                                className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                                    pageNum === totalPages || isLoading
                                        ? 'bg-gray-100 dark:bg-slate-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                        : 'bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-600 border border-gray-300 dark:border-slate-600'
                                }`}
                                onClick={() => handlePageNumChange(totalPages)}
                                disabled={pageNum === totalPages || isLoading}
                            >
                                Last
                            </button>
                        </li>
                    </ul>
                </nav>
            </div>
        </div>
    );
};

export default Pagination;